import React, { useEffect, useState } from "react";
import { Rss, Loader2, Copy, RefreshCw, ExternalLink, ShieldCheck, AlertTriangle, Code } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { appConfirm } from "@/components/AppDialogHost";

/**
 * RssFeeds — surfaces every read-only RSS endpoint for the current
 * station so radio-automation operators can paste the URL into
 * Myriad / RCS Zetta / OtsAV / AzuraCast / SAM Broadcaster.
 */
export default function RssFeeds() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rotating, setRotating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/rss/feeds");
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load feeds");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const copy = async (text, label = "URL copied") => {
    try {
      await navigator.clipboard.writeText(text || "");
      toast.success(label);
    } catch { toast.error("Copy failed"); }
  };

  const rotate = async () => {
    if (!await appConfirm("Rotate token? Every existing RSS subscription URL will stop working until you paste the new one.")) return;
    setRotating(true);
    try {
      await api.post("/rss/rotate-token");
      toast.success("New token minted — refreshed feed URLs below");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rotate failed");
    } finally { setRotating(false); }
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto animate-fade-up" data-testid="rss-feeds-page">
      <PageHeader
        eyebrow="// RSS FEEDS"
        title="Plug BroadcastFlow into any automation system."
        description="Read-only RSS 2.0 endpoints for Myriad · RCS Zetta · OtsAV · AzuraCast · SAM Broadcaster. Paste the URL into your automation's news ticker, cart-wall RSS importer, or scheduled metadata input."
        action={
          <button
            onClick={rotate}
            disabled={rotating || loading || !data}
            data-testid="rss-rotate-token"
            className="inline-flex items-center gap-2 rounded-md border border-rose-500/40 bg-rose-500/10 px-4 py-2 text-xs text-rose-300 hover:bg-rose-500/20 transition disabled:opacity-50"
          >
            {rotating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            Rotate token
          </button>
        }
      />

      {loading ? (
        <div className="text-center py-20 text-ink-muted font-mono text-xs">LOADING FEEDS...</div>
      ) : !data ? (
        <div className="rounded-xl border border-line bg-bg-surface p-8 text-center">
          <AlertTriangle className="h-8 w-8 mx-auto text-amber-400 mb-3" />
          <div className="text-sm text-ink-secondary">Couldn&apos;t load feeds. Make sure you have an active station selected.</div>
        </div>
      ) : (
        <>
          {/* Token banner */}
          <div className="mb-6 rounded-xl border border-cyan-500/40 bg-cyan-500/5 p-4 flex items-start gap-3" data-testid="rss-token-banner">
            <ShieldCheck className="h-5 w-5 text-cyan-300 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-cyan-300">// ACCESS TOKEN</span>
                <span className="font-mono text-xs bg-bg-elevated border border-line rounded px-2 py-0.5 break-all" data-testid="rss-token-value">
                  {data.token}
                </span>
                <button
                  onClick={() => copy(data.token, "Token copied")}
                  className="text-[11px] text-cyan-300 hover:text-cyan-200 inline-flex items-center gap-1"
                  data-testid="rss-token-copy"
                >
                  <Copy className="h-3 w-3" /> Copy
                </button>
              </div>
              <p className="mt-2 text-xs text-ink-secondary">
                Append <code className="text-cyan-300">?token={data.token.slice(0, 14)}…</code> to every feed URL. Anyone with the URL can read the feed — keep it private to your automation system. Rotate to invalidate all subscriptions at once.
              </p>
            </div>
          </div>

          {/* Feed list */}
          <div className="space-y-4" data-testid="rss-feed-list">
            {data.feeds.map((f) => (
              <article
                key={f.id}
                className="rounded-xl border border-line bg-bg-surface p-5"
                data-testid={`rss-feed-${f.id}`}
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-brand-amber/15 text-brand-amber grid place-items-center">
                      <Rss className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="font-display text-lg font-bold">{f.label}</h3>
                      <p className="text-xs text-ink-muted">{f.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <a
                      href={f.url}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`rss-feed-open-${f.id}`}
                      className="inline-flex items-center gap-1.5 rounded-md border border-line bg-bg-elevated px-3 py-1.5 text-[11px] hover:text-cyan-300 transition"
                    >
                      <ExternalLink className="h-3 w-3" /> Open XML
                    </a>
                    <button
                      onClick={() => copy(f.url, `${f.label} feed URL copied`)}
                      data-testid={`rss-feed-copy-${f.id}`}
                      className="inline-flex items-center gap-1.5 rounded-md bg-brand-amber px-3 py-1.5 text-[11px] font-semibold text-black amber-glow"
                    >
                      <Copy className="h-3 w-3" /> Copy URL
                    </button>
                  </div>
                </div>
                <div className="rounded-lg border border-line bg-bg p-3 font-mono text-[11px] text-ink-secondary break-all">
                  {f.url}
                </div>
              </article>
            ))}
          </div>

          {/* Setup instructions */}
          <div className="mt-8 rounded-xl border border-line bg-bg-surface p-5" data-testid="rss-setup-instructions">
            <div className="flex items-center gap-2 mb-3">
              <Code className="h-4 w-4 text-cyan-300" />
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-cyan-300">// SETUP CHEATSHEET</span>
            </div>
            <ul className="text-xs text-ink-secondary space-y-2 list-disc list-inside ml-1">
              <li><strong className="text-ink-primary">Myriad Playout</strong>: <span className="text-ink-muted">Tools → RSS News Reader → Add Source → paste feed URL → set refresh interval to 5 min.</span></li>
              <li><strong className="text-ink-primary">RCS Zetta / GSelector</strong>: <span className="text-ink-muted">Right-click on a category → Import RSS → paste URL → enable hourly poll.</span></li>
              <li><strong className="text-ink-primary">OtsAV / AutoDJ</strong>: <span className="text-ink-muted">Scheduled task → URL grabber → save the &lt;description&gt; to a text file for the ticker overlay.</span></li>
              <li><strong className="text-ink-primary">AzuraCast</strong>: <span className="text-ink-muted">Station Profile → Now-Playing Backend Tools → Custom Frontend URL → paste feed for offline mirroring.</span></li>
              <li><strong className="text-ink-primary">SAM Broadcaster</strong>: <span className="text-ink-muted">Window → RSS Reader → Add Feed → paste URL → drag headlines into the schedule.</span></li>
              <li><strong className="text-ink-primary">Generic</strong>: <span className="text-ink-muted">Feeds refresh on read with 60-second edge cache + 10-minute TTL. Always include the <code className="text-cyan-300">?token=…</code> query parameter.</span></li>
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
